import React, { useContext } from 'react';
import { Spin } from 'antd';
import { useLocation } from 'react-router-dom';
import {
  PERMISSION_SECTIONS,
  getProductIdsForSection,
  resolveSectionIdFromPath,
  stripAccountPrefix,
} from '../../constants/permissions';
import { ProductsContext } from '../../context/ProductsContext';
import { useActiveAccountOptional } from '../../hooks/useActiveAccount';
import { hasVirtualAgentProduct } from '../../utils/virtualAgentPlan';
import ProductNotEnabled from './ProductNotEnabled';

const normalizeKey = (value) => {
  if (!value) return '/';
  const str = String(value).trim();
  const withSlash = str.startsWith('/') ? str : `/${str}`;
  return stripAccountPrefix(withSlash).toLowerCase();
};

const productCodeMatches = (owned, wanted) => {
  if (!owned || !wanted) return false;
  const a = String(owned);
  const b = String(wanted);
  return a === b || a.startsWith(b) || b.startsWith(a);
};

const clientOwnsCode = (products, wanted) => {
  if (!wanted || !Array.isArray(products)) return false;
  return products.some((product) => productCodeMatches(product, wanted));
};

const catalogProductCoversPath = (product, pathname) => {
  const views = product?.views || [];
  if (views.length > 0) {
    return views.some((view) => {
      const viewPath = normalizeKey(view.path);
      return (
        pathname === viewPath ||
        pathname.startsWith(`${viewPath}/`) ||
        viewPath.startsWith(`${pathname}/`)
      );
    });
  }

  const name = String(product?.comercialName || '').trim();
  if (!name) return false;
  const fallback = normalizeKey(`service/${name.replace(/\s+/g, '-')}`);
  return pathname === fallback || pathname.startsWith(`${fallback}/`);
};

const RequireProduct = ({ productId, children }) => {
  const location = useLocation();
  const account = useActiveAccountOptional();
  const { allProducts, loading: productsLoading } = useContext(ProductsContext) || {};
  const pathname = normalizeKey(location.pathname);
  const sectionId = resolveSectionIdFromPath(pathname);
  const section = PERMISSION_SECTIONS.find((item) => item.id === sectionId);
  const clientProducts = account?.clientInfo?.products;

  if (account?.clientLoading || productsLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
        <Spin />
      </div>
    );
  }

  const codes = [];
  if (productId) {
    codes.push(productId);
  } else {
    codes.push(...getProductIdsForSection(section));
  }

  const ownsListedCode = codes.some((code) => clientOwnsCode(clientProducts, code));
  const ownsVirtualAgent =
    Boolean(sectionId?.startsWith('virtual-agent')) &&
    hasVirtualAgentProduct(clientProducts);

  const ownsCatalogProduct = (allProducts || []).some((product) => {
    const catalogId = product?.id || product?.code || product?.productId;
    if (!catalogId || !clientOwnsCode(clientProducts, catalogId)) {
      return false;
    }
    return catalogProductCoversPath(product, pathname);
  });

  const gated = codes.length > 0 || Boolean(sectionId?.startsWith('virtual-agent'));
  const enabled =
    !gated || ownsListedCode || ownsVirtualAgent || ownsCatalogProduct;

  if (!enabled) {
    const catalogMatch = (allProducts || []).find((product) => {
      const catalogId = product?.id || product?.code || product?.productId;
      return codes.some((code) => productCodeMatches(catalogId, code));
    });
    const productName =
      catalogMatch?.comercialName || section?.label || null;

    return (
      <ProductNotEnabled
        productName={productName}
        accountName={account?.clientInfo?.name}
      />
    );
  }

  return children;
};

export default RequireProduct;
